/**
 * Scaled Theme Loader v2.1
 * Server-delivered sections for maximum security
 *
 * This file will be obfuscated before distribution.
 */

(function() {
  'use strict';

  const LOADER_VERSION = '2.5.0';
  console.log('[Scaled] Loader version:', LOADER_VERSION);

  const CONFIG = window.ScaledConfig || {};
  const API_URL = CONFIG.apiUrl || 'https://scaledinfo-backend-production.up.railway.app';
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // Session storage keys
  const STORAGE_KEY = 'scaled_session';
  const CACHE_KEY = 'scaled_sections_cache';

  /**
   * Simple hash function for integrity checks
   */
  function simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  /**
   * Generate HMAC signature for request
   */
  async function generateSignature(payload, timestamp) {
    const message = `${timestamp}.${JSON.stringify(payload)}`;
    const encoder = new TextEncoder();
    const data = encoder.encode(message);

    // Use domain as part of the key (domain-locked)
    const keyData = encoder.encode(CONFIG.shopDomain + CONFIG.licenseKey);
    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', key, data);
    return Array.from(new Uint8Array(signature))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Get or create session token
   */
  function getSession() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        if (session.expires > Date.now()) {
          return session;
        }
      }
    } catch (e) {
      // Session storage not available
    }
    return null;
  }

  /**
   * Store session token
   */
  function setSession(session) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (e) {
      // Session storage not available
    }
  }

  /**
   * Get cached sections
   */
  function getCachedSections() {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        if (data.expires > Date.now()) {
          return data.sections;
        }
      }
    } catch (e) {
      // Cache not available
    }
    return null;
  }

  /**
   * Cache sections
   */
  function cacheSections(sections) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        sections,
        expires: Date.now() + CACHE_TTL
      }));
    } catch (e) {
      // Cache not available
    }
  }

  /**
   * Collect section data from the page
   */
  function collectSectionData() {
    const sections = {};

    document.querySelectorAll('[data-scaled-section]').forEach(el => {
      const sectionName = el.dataset.scaledSection;
      const wrapper = el.closest('[class*="-wrapper"]');

      // Initialize section data
      let sectionData = { settings: {} };

      // First, look for dedicated section settings script (new approach)
      const settingsScript = document.querySelector(`script[data-scaled-section-settings="${sectionName}"]`);
      if (settingsScript) {
        try {
          const parsed = JSON.parse(settingsScript.textContent);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            sectionData.settings = parsed;
            console.log('[Scaled] Found settings for', sectionName, ':', Object.keys(parsed));
          }
        } catch (e) {
          console.warn('[Scaled] Failed to parse section settings for', sectionName, ':', e);
        }
      }
      // Fallback: Collect Shopify section settings from wrapper (old approach)
      else if (wrapper) {
        const schemaScript = wrapper.querySelector('script[type="application/json"]:not([data-scaled-products]):not([data-scaled-section-settings])');
        if (schemaScript) {
          try {
            const parsed = JSON.parse(schemaScript.textContent);
            // Only use if it's an object, not an array (products data is an array)
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
              sectionData.settings = parsed;
            }
          } catch (e) {
            // Keep empty settings
          }
        }
      }

      // Special handling for sections that need products data
      if (sectionName === 'product-grid' || sectionName === 'live-sales') {
        // Look for products script - try multiple locations
        let productsScript = el.parentElement?.querySelector('[data-scaled-products]');
        if (!productsScript) {
          productsScript = document.querySelector('[data-scaled-products]');
        }

        if (productsScript) {
          try {
            const productsText = productsScript.textContent.trim();
            if (productsText && productsText !== '[]') {
              sectionData.settings.products = JSON.parse(productsText);
              console.log('[Scaled] Found', sectionData.settings.products.length, 'products for', sectionName);
            } else {
              console.warn('[Scaled] Products data is empty for', sectionName);
            }
          } catch (e) {
            console.warn('[Scaled] Failed to parse products data for', sectionName, ':', e);
          }
        } else {
          console.warn('[Scaled] Products script not found for', sectionName);
        }
      }

      sections[sectionName] = sectionData;
    });

    return sections;
  }

  /**
   * Fetch sections from API
   */
  async function fetchSections(sectionNames) {
    // Collect section data first (we need this for cache validation)
    const sectionData = collectSectionData();

    // Debug: Log what we collected
    console.log('[Scaled] Section data collected:', Object.keys(sectionData));
    if (sectionData['product-grid']) {
      const products = sectionData['product-grid'].settings?.products;
      console.log('[Scaled] Products in section data:', products ? products.length : 'none');
    }

    // Create a cache key based on products data for product-grid
    const productsHash = sectionData['product-grid']?.settings?.products
      ? simpleHash(JSON.stringify(sectionData['product-grid'].settings.products))
      : 'no-products';

    // Create a config hash based on colors, settings, and section settings that affect rendering
    // Include all section settings (excluding products which has its own hash)
    const sectionSettingsForHash = {};
    Object.entries(sectionData).forEach(([name, data]) => {
      const { products, ...settingsWithoutProducts } = data.settings || {};
      sectionSettingsForHash[name] = settingsWithoutProducts;
    });

    const configForHash = {
      colors: CONFIG.colors,
      accentColor: CONFIG.accentColor,
      secondaryColor: CONFIG.secondaryColor,
      brandName: CONFIG.brandName,
      chatbot: CONFIG.chatbot,
      urgency: CONFIG.urgency,
      sectionSettings: sectionSettingsForHash
    };
    const configHash = simpleHash(JSON.stringify(configForHash));

    console.log('[Scaled] Products hash:', productsHash);
    console.log('[Scaled] Config hash:', configHash);
    console.log('[Scaled] Section settings in hash:', Object.keys(sectionSettingsForHash));

    // Check cache first - must match both products AND config
    const cached = getCachedSections();
    if (cached && cached._productsHash === productsHash && cached._configHash === configHash) {
      const allCached = sectionNames.every(name => cached[name]);
      if (allCached) {
        console.log('[Scaled] Using cached sections');
        return cached;
      }
    }

    const timestamp = Date.now();
    const payload = {
      licenseKey: CONFIG.licenseKey,
      domain: CONFIG.shopDomain,
      sections: sectionNames,
      settings: sectionData,
      config: {
        accentColor: CONFIG.accentColor,
        secondaryColor: CONFIG.secondaryColor,
        brandName: CONFIG.brandName,
        chatbot: CONFIG.chatbot,
        urgency: CONFIG.urgency
      }
    };

    const signature = await generateSignature(payload, timestamp);

    const session = getSession();
    const headers = {
      'Content-Type': 'application/json',
      'X-Timestamp': timestamp.toString(),
      'X-Signature': signature,
      'X-Domain': CONFIG.shopDomain
    };

    if (session?.token) {
      headers['X-Session-Token'] = session.token;
    }

    console.log('[Scaled] Making API request to:', `${API_URL}/api/v2/theme-sections/render`);
    console.log('[Scaled] Payload sections:', payload.sections);
    console.log('[Scaled] Products count in payload:', payload.settings?.['product-grid']?.settings?.products?.length || 0);

    const requestBody = JSON.stringify(payload);
    console.log('[Scaled] Request body size:', requestBody.length, 'bytes');

    const response = await fetch(`${API_URL}/api/v2/theme-sections/render`, {
      method: 'POST',
      headers,
      body: requestBody
    });

    console.log('[Scaled] Response status:', response.status);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('[Scaled] Response sections:', Object.keys(data.html || {}));
    if (data.html?.['product-grid']) {
      const hasPlaceholder = data.html['product-grid'].includes('product-card--placeholder');
      const hasRealProducts = data.html['product-grid'].includes('product-card__title');
      console.log('[Scaled] product-grid has placeholders:', hasPlaceholder);
      console.log('[Scaled] product-grid HTML length:', data.html['product-grid'].length);
    }

    // Store session token
    if (data.session) {
      setSession({
        token: data.session.token,
        expires: Date.now() + (data.session.expiresIn * 1000)
      });
    }

    // Cache sections with products hash AND config hash for validation
    if (data.html) {
      const cacheData = { ...data.html, _productsHash: productsHash, _configHash: configHash };
      cacheSections(cacheData);
    }

    return data.html || {};
  }

  /**
   * Inject section HTML into shell containers
   */
  function injectSections(sections) {
    Object.entries(sections).forEach(([name, html]) => {
      const container = document.querySelector(`[data-scaled-section="${name}"]`);
      if (container) {
        container.innerHTML = html;
        container.classList.remove('scaled-shell--loading');

        // Execute any inline scripts
        container.querySelectorAll('script').forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });

        // Dispatch event for section loaded
        container.dispatchEvent(new CustomEvent('scaled:section:loaded', {
          detail: { section: name },
          bubbles: true
        }));
      }
    });
  }

  /**
   * Show error state in shells
   */
  function showError(message) {
    document.querySelectorAll('.scaled-shell').forEach(el => {
      el.classList.remove('scaled-shell--loading');
      el.innerHTML = `
        <div class="scaled-license-error">
          <div class="scaled-license-error__icon">⚠️</div>
          <div class="scaled-license-error__message">${escapeHtml(message)}</div>
        </div>
      `;
    });
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Initialize loader
   */
  async function init() {
    // Validate config
    if (!CONFIG.licenseKey) {
      showError('License key required. Add your key in theme settings.');
      return;
    }

    if (!CONFIG.shopDomain) {
      showError('Configuration error. Please contact support.');
      return;
    }

    // Find all section shells
    const shells = document.querySelectorAll('[data-scaled-section]');
    if (shells.length === 0) {
      return;
    }

    const sectionNames = Array.from(shells).map(el => el.dataset.scaledSection);

    try {
      const sections = await fetchSections(sectionNames);
      injectSections(sections);

      // Dispatch global event
      document.dispatchEvent(new CustomEvent('scaled:loaded', {
        detail: { sections: sectionNames }
      }));
    } catch (error) {
      console.error('[Scaled] Error loading sections:', error);

      if (error.message.includes('license')) {
        showError('Invalid license. Please check your license key.');
      } else if (error.message.includes('domain')) {
        showError('License not activated for this domain.');
      } else {
        showError('Unable to load content. Please refresh the page.');
      }
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging (will be removed in obfuscated version)
  window.__ScaledLoader = {
    version: LOADER_VERSION,
    init,
    fetchSections,
    getSession
  };

})();
